import type {
  AxisRangeHint,
  PayloadShape,
  PayloadShapeKind,
  RecordFieldRole,
} from "./payload-shape";
import { PayloadShape as Shapes } from "./payload-shape";

/** Metadata for one named plot series. */
export type SeriesDescriptor<K extends PayloadShapeKind = PayloadShapeKind> = Readonly<{
  key: string;
  label: Option<string>;
  shape: Extract<PayloadShape, { kind: K }>;
  yRange: Option<AxisRangeHint>;
  unit: Option<string>;
}>;

export namespace SeriesDescriptor {
  export type Init<K extends PayloadShapeKind = PayloadShapeKind> = Readonly<{
    key: string;
    label: Option<string>;
    shape: Extract<PayloadShape, { kind: K }>;
    yRange: Option<AxisRangeHint>;
    unit: Option<string>;
  }>;

  export function make<K extends PayloadShapeKind>(
    init: Init<K>,
  ): SeriesDescriptor<K> {
    return Object.freeze({
      key: init.key,
      label: init.label,
      shape: init.shape,
      yRange: init.yRange,
      unit: init.unit,
    });
  }

  export function scalar(
    key: string,
    label: Option<string> = null,
    yRange: Option<AxisRangeHint> = null,
    unit: Option<string> = null,
  ): SeriesDescriptor<"scalar"> {
    return make({
      key,
      label,
      shape: Shapes.SCALAR,
      yRange,
      unit,
    });
  }

  export function barWidth(
    key: string,
    label: Option<string> = null,
    yRange: Option<AxisRangeHint> = null,
    unit: Option<string> = null,
  ): SeriesDescriptor<"bar-width"> {
    return make({
      key,
      label,
      shape: Shapes.BAR_WIDTH,
      yRange,
      unit,
    });
  }

  export function rangeY(
    key: string,
    label: Option<string> = null,
    yRange: Option<AxisRangeHint> = null,
    unit: Option<string> = null,
  ): SeriesDescriptor<"range-y"> {
    return make({
      key,
      label,
      shape: Shapes.RANGE_Y,
      yRange,
      unit,
    });
  }

  export function ohlc(
    key: string,
    label: Option<string> = null,
    yRange: Option<AxisRangeHint> = null,
    unit: Option<string> = null,
  ): SeriesDescriptor<"ohlc"> {
    return make({
      key,
      label,
      shape: Shapes.OHLC,
      yRange,
      unit,
    });
  }

  export function point(
    key: string,
    label: Option<string> = null,
    yRange: Option<AxisRangeHint> = null,
    unit: Option<string> = null,
  ): SeriesDescriptor<"point"> {
    return make({
      key,
      label,
      shape: Shapes.POINT,
      yRange,
      unit,
    });
  }

  export function record(
    key: string,
    fields: Readonly<Record<string, RecordFieldRole>>,
    label: Option<string> = null,
    yRange: Option<AxisRangeHint> = null,
    unit: Option<string> = null,
  ): SeriesDescriptor<"record"> {
    return make({
      key,
      label,
      shape: Shapes.record(fields),
      yRange,
      unit,
    });
  }
}
